/* import __COLOCATED_TEMPLATE__ from './project-details-details.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { task } from 'ember-concurrency';
import {
  PROJECT_DETAILS__APPLICATION_EDITABLE_STATES_SELLER,
  PROJECT_DETAILS__APPLICATION_EDITABLE_STORY_CODE_STATES_BUYER,
} from "sh-www/constants/routes/project-details";
import { isApplicationEditable } from "sh-www/helpers/applications/is-application-editable";
import { handleErrorResponseWithModal } from 'sh-www/utils/handles-errors';
import { showModal } from 'sh-www/utils/modal-controls';
import { htmlSafe } from '@ember/template';

const CLASS_NAME = 'SH-c-project-details-details';

class ProjectDetailsDetails extends Component {
  @service('state-managers/application') applicationStateManager;
  @service('commands/application-commands') applicationCommandService;
  @service('error-tracker') errorTracker;
  @service session;
  @service store;

  get className() {
    return CLASS_NAME;
  }

  get canEditCodes() {
    return this.session.currentUser.isBuyer && PROJECT_DETAILS__APPLICATION_EDITABLE_STORY_CODE_STATES_BUYER.includes(this.args.application.status);
  }

  get hasAnyCostOrStoryCodes() {
    return this.args.application.company.usesCostCodes;
  }

  get canEditLocation() {
    return this.session.currentUser.isBuyer
      ? true
      : PROJECT_DETAILS__APPLICATION_EDITABLE_STATES_SELLER.includes(this.args.application.status);
  }

  get canEditServices() {
    return isApplicationEditable(this.args.application, this.session.currentUser.isBuyer);
  }

  get codesDropdownOptions() {
    return [{
      title: 'Edit',
      onClick: () => this.onEditStoryCodesClicked(),
    }];
  }

  get companyTeamsDropdownOptions() {
    return [{
      title: 'Edit',
      onClick: () => this.onEditCompanyTeams(),
    }];
  }

  get locationDropdownOptions() {
    return [{
      title: 'Edit',
      onClick: () => this.onEditLocationClicked(),
    }];
  }

  get servicesDropdownOptions() {
    return [{
      title: 'Edit',
      onClick: () => this.onEditServicesClicked(),
    }];
  }

  get showTeamModule() {
    return this.session.currentUser.isBuyer && this.args.application.company.canUseCompanyTeams;
  }

  get locationText() {
    const { location } = this.args.application;
    if (!location || !location.placeId) {
      return 'Not selected';
    }

    if (location.locationType === 'country') {
      return 'Not selected';
    }

    return location.formattedAddress;
  }

  get descriptionLinkified() {
    const { application } = this.args;

    // determine whether to use memorialized version
    return htmlSafe(application.applicationDetails?.descriptionLinkified ?? application.assignment.descriptionLinkified);
  }

  get examplesLinkified() {
    const { application } = this.args;

    // determine whether to use memorialized version
    return htmlSafe(application.applicationDetails?.examplesLinkified ?? application.assignment.examplesLinkified);
  }

  @action
  onEditCompanyTeams() {
    const { assignment, company } = this.args.application;

    showModal(this, 'projects/edit-company-team-modal', {
      assignment,
      company,
    });
  }

  @action
  onEditStoryCodesClicked() {
    const modalData = {
      actorUser: this.session.currentUser,
      application: this.args.application,
      onStoryCodesUpdated: () => {
        this.applicationStateManager.addToast('Code updated');
      }
    };
    showModal(this, 'projects/edit-story-codes-modal', modalData);
  }

  @action
  onEditLocationClicked() {
    const { application } = this.args;
    const modalData = {
      actorUser: this.session.currentUser,
      application,
      selectedLocation: application.location,
      onLocationUpdated: (selectedLocation) => {
        this.handleLocationUpdated.perform(application, selectedLocation);
      },
      onLocationDeleted: () => {
        this.handleLocationDeleted.perform(application);
      },
    };
    showModal(this, 'projects/edit-location-modal', modalData);
  }

  @task
  *handleLocationUpdated(application, location) {
    try {
      yield this.applicationCommandService.updateApplicationLocation(application, location);
      yield this.args.loadApplicationTask.perform(application.id, ['location']);
      this.applicationStateManager.addToast('Location updated');
    } catch (e) {
      handleErrorResponseWithModal(
        this,
        e,
        'There was an error updating the location. Please refresh and try again.',
        `handleLocationUpdated -- error updating app id: ${application.id}`
      );
    }
  }

  @task
  *handleLocationDeleted(application) {
    try {
      yield this.applicationCommandService.deleteApplicationLocation(application);
      yield this.args.loadApplicationTask.perform(application.id, ['location']);
      this.applicationStateManager.addToast('Location removed');
    } catch (e) {
      handleErrorResponseWithModal(
        this,
        e,
        'There was an error removing the location. Please refresh and try again.',
        `handleLocationDeleted -- error updating app id: ${application.id}`
      );
    }
  }

  @action
  onEditServicesClicked() {
    const { application } = this.args;
    const modalData = {
      actorUser: this.session.currentUser,
      application: application,
      selectedServices: application.services,
      onServicesUpdated: (selectedServices) => {
        this.handleServicesUpdated.perform(application, selectedServices);
      },
    };

    showModal(this, 'projects/edit-services-modal', modalData);
  }

  @task
  *handleServicesUpdated(application, newServices) {
    try {
      const newServiceIDs = newServices.map((service) => service.id);
      yield this.applicationCommandService.updateApplication(application, { services: newServiceIDs });
      yield this.args.loadApplicationTask.perform(application.id, ['services']);
      this.applicationStateManager.addToast('Services updated');
    } catch (e) {
      this.applicationStateManager.addToast('There was a problem updating services. Please try again.');
      this.errorTracker.error('handleServicesUpdated - ' + application.id, this, e);
    }
  }
}

export default ProjectDetailsDetails;
